import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { usePlatformCatalogAccess } from 'src/graphql/entities/group-rules/platform-catalog-access';
import { QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useGroupRulesStore } from 'src/store/group-rules';
import type { UserGroupOption } from './user-group.types';

export const USER_GROUP_FIELDS = gql`
  fragment UserGroupFields on UserGroup {
    id
    name
    isPublic
    partnerId
    ruleValues {
      id
      userGroupRuleId
      value
    }
  }
`;

const GET_USER_GROUP_VALUES_QUERY = gql`
  query GetUserGroup($params: QueryParams) @entity(name: "UserGroup") {
    getUserGroup(queryParams: $params) {
      records {
        ...UserGroupFields
      }
    }
  }
  ${USER_GROUP_FIELDS}
`;

interface GetUserGroupQuery {
  getUserGroup: Records<{
    id: string;
    name: string;
    isPublic: boolean;
    partnerId: number;
  }>;
}

export const useUserGroups = (options?: QueryHookOptions<GetUserGroupQuery>) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { includePlatformLevelData } = usePlatformCatalogAccess(
    'UserGroup',
    selectedPartnerId,
  );

  const { data, ...rest } = useQuery<GetUserGroupQuery, QueryVariables>(
    GET_USER_GROUP_VALUES_QUERY,
    {
      ...options,
      variables: {
        params: {
          limit: MAX_QUERY_LIMIT,
        },
      },
      context: {
        includePlatformLevelData,
      },
    },
  );

  const userGroups = data?.getUserGroup.records;

  return { userGroups, ...rest };
};

/**
 * User groups as picker options, filtered to what the current user may offer:
 * public groups always, private groups only for admins holding
 * `access_to_private_user_grups`. Platform groups keep the existing
 * `usePlatformCatalogAccess('UserGroup')` partner gating inherited from
 * {@link useUserGroups}.
 */
export function useUserGroupOptions(options?: QueryHookOptions<GetUserGroupQuery>): {
  userGroupOptions: UserGroupOption[];
  loading: boolean;
} {
  const { userGroups, loading } = useUserGroups(options);
  const canSeePrivateGroups = useGroupRulesStore(
    (state) => state.userGroupRules[UserGroupRules.AccessToPrivateUserGrups] === true,
  );

  return useMemo(() => {
    if (!userGroups) return { userGroupOptions: [], loading };

    const userGroupOptions = userGroups
      .filter((group) => group.isPublic !== false || canSeePrivateGroups)
      .map((group) => ({
        id: group.id,
        value: group.id,
        label: group.name,
        partnerId: group.partnerId,
      }));

    return { userGroupOptions, loading };
  }, [canSeePrivateGroups, loading, userGroups]);
}
